import { memo, FormEvent, useEffect, useState } from 'react';
import type { FC } from 'react';

import resets from '../_resets.module.css';
import { Button_ContentTextSizeLargeTyp } from './Button_ContentTextSizeLargeTyp/Button_ContentTextSizeLargeTyp';
import { CSLOGO_ColoredTrueTextTrueHori } from './CSLOGO_ColoredTrueTextTrueHori/CSLOGO_ColoredTrueTextTrueHori';
import classes from './Frame1.module.css';
import { Layer3Icon } from './Layer3Icon';
import config from '../../config';
import { signIn, requestPasswordReset, changePassword } from '../../api/auth';
import { buildPasswordRules, validatePasswordAggressive } from '../_shared/passwordFlow';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';


interface Props {
  className?: string;
}

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

export const Frame1: FC<Props> = memo(function Frame1(props = {}) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [expiredNotice, setExpiredNotice] = useState(false);

  // N-07 forced first-sign-in password change. When SignIn succeeds but the
  // user is flagged mustChangePassword, the session is held in pendingSignIn
  // (NOT stored) and this screen replaces the login form until the change
  // succeeds — there is no way into the app around it.
  const [changeMode, setChangeMode] = useState(false);
  const [pendingSignIn, setPendingSignIn] = useState<any>(null);
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [changeLoading, setChangeLoading] = useState(false);

  // One-shot flag set by expireSession() (utils/auth.ts) when a 401/403 or a
  // dead-session save kicked the user here. Explains the redirect; the
  // stashed pendingDeepLink returns them to where they were after sign-in.
  useEffect(() => {
    try {
      if (localStorage.getItem('sessionExpired') === '1') {
        localStorage.removeItem('sessionExpired');
        setExpiredNotice(true);
      }
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const handleForgotPassword = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (!forgotEmail.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setForgotLoading(true);
    try {
      await requestPasswordReset(forgotEmail.trim());
      setForgotSent(true);
      toast.success('If an account exists with that email, a reset link has been sent.');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const completeSignIn = (signInData: any) => {
    localStorage.setItem('user', JSON.stringify(signInData));
    setEmail('');
    setPassword('');

    // Resume a deferred deep-link if the user landed on /login from a
    // notification email link (e.g. /audit-log?id=<operationId>) without
    // an active session. Set on the AuditLog component below.
    const pending = localStorage.getItem('pendingDeepLink');
    if (pending) {
      localStorage.removeItem('pendingDeepLink');
      navigate(pending);
      return;
    }
    navigate('/dashboard');
  };

  const handleForcedChange = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    const identity = {
      email,
      firstName: pendingSignIn?.user?.firstName,
      lastName: pendingSignIn?.user?.lastName,
    };
    const policyError = validatePasswordAggressive(newPw.trim(), identity);
    if (policyError) {
      setError(policyError);
      return;
    }
    if (newPw.trim() !== confirmPw.trim()) {
      setError('Passwords do not match');
      return;
    }
    setChangeLoading(true);
    try {
      await changePassword(email, password, newPw.trim());
      const signInData = { ...pendingSignIn, user: { ...pendingSignIn.user, mustChangePassword: false } };
      setChangeMode(false);
      setPendingSignIn(null);
      setNewPw('');
      setConfirmPw('');
      toast.success('Password changed. Welcome!');
      completeSignIn(signInData);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to change password. Please try again.');
    } finally {
      setChangeLoading(false);
    }
  };

  const submitForm = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    // Mock authentication bypass for local development
    if (config.useMockAuth) {
      if (email !== config.mockEmail || password !== config.mockPassword) {
        setError('Invalid email or password');
        return;
      }
      localStorage.setItem('user', JSON.stringify(config.mockUser));
      setEmail('');
      setPassword('');
      navigate('/dashboard');
      return;
    }

    try {
      const signInData = await signIn(email, password);

      // Transition shim: SixSigma API (post-d0a4f7a) returns role as a string,
      // OBI/CCS APIs still return role as { name }. Remove the object branch
      // once OBI/CCS are on the new API.
      const role = signInData?.user?.role;
      const roleName = typeof role === 'string' ? role : role?.name;
      // Role-name drift: backend RoleModel enum is "Administrator", admin
      // panel NewUser uses "Admin". Either is a legitimate admin — accept both
      // until the schemas are unified. Case-insensitive to absorb any drift.
      const isAdmin = roleName?.toLowerCase() === 'admin'
        || roleName?.toLowerCase() === 'administrator';
      // Auditor (Final Review tier, 2026-08-23): read-only access to the
      // whole panel; every mutation control is hidden via
      // _shared/permissions.canMutate and the API refuses their writes.
      const isAuditor = roleName?.toLowerCase() === 'auditor';
      if (!isAdmin && !isAuditor) {
        setError('Access denied. Only Admin users can log in to the admin panel.');
        return;
      }

      // N-07: flagged users must set their own password before the session
      // is stored or any navigation happens.
      if (signInData?.user?.mustChangePassword) {
        setPendingSignIn(signInData);
        setChangeMode(true);
        setError('');
        return;
      }

      completeSignIn(signInData);
    } catch (error: any) {
      console.error('API Request Error:', error);
      if (error.code === 'ERR_NETWORK' || !error.response) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (error.response?.status === 401 || error.response?.status === 400) {
        setError('Invalid email or password. Please try again.');
      } else if (error.response?.status >= 500) {
        setError('The server is temporarily unavailable. Please try again later.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <div className={`${resets.clapyResets} ${classes.root}`}>
      <div className={classes.frame2}>
        <CSLOGO_ColoredTrueTextTrueHori
          className={classes.cSLOGO}
          classes={{ layer3: classes.layer3 }}
          swap={{
            layer3: (
              <div className={classes.layer3}>
                <Layer3Icon className={classes.icon} />
              </div>
            ),
          }}
        />
      </div>
      <div className={classes.line1}></div>
      <div className={classes.heading}>
        <div className={classes.welcome}>{changeMode ? 'Create your new password' : forgotMode ? 'Reset Password' : 'Welcome'}</div>
        {/* The base class is white-space:nowrap/min-content, sized for the
            short login tagline — the longer N-07 subtitle needs to wrap. */}
        <div className={classes.loginToGetStarted} style={changeMode ? { whiteSpace: 'normal', width: 'auto', maxWidth: '400px' } : undefined}>
          {changeMode
            ? 'Your administrator set a temporary password. Choose your own to continue.'
            : forgotMode
            ? (forgotSent ? 'Check your email for a reset link.' : 'Enter your email to receive a reset link.')
            : 'Login to get started.'}
        </div>
      </div>

      {changeMode ? (
        <form style={formStyle} onSubmit={handleForcedChange}>
          {error && (
            <div
              role="alert"
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '8px',
                backgroundColor: '#FFEBEE', color: '#C62828', border: '1px solid #EF9A9A',
                borderRadius: '8px', padding: '12px 14px', marginBottom: '16px',
                fontSize: '13px', lineHeight: 1.4, textAlign: 'left',
              }}
            >
              <span aria-hidden="true" style={{ fontWeight: 700, lineHeight: 1.3 }}>!</span>
              <span>{error}</span>
            </div>
          )}
          <div className={`${classes.inputs} ${classes.row}`}>
            <label className={classes.formLabel}>New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className={classes.formControl}
                placeholder="New Password"
                type={showNewPw ? 'text' : 'password'}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                style={{ paddingRight: '44px' }}
              />
              <button
                type="button"
                onClick={() => setShowNewPw(prev => !prev)}
                style={{
                  position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                  fontSize: '18px', color: '#707070',
                }}
                tabIndex={-1}
                aria-label={showNewPw ? 'Hide password' : 'Show password'}
              >
                {showNewPw ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {buildPasswordRules({ email, firstName: pendingSignIn?.user?.firstName, lastName: pendingSignIn?.user?.lastName }).map(rule => {
                const ok = rule.test(newPw);
                return (
                  <span key={rule.label} style={{ fontSize: '11px', color: ok ? '#27ae60' : '#707070', display: 'block' }}>
                    {ok ? '\u2713' : '\u2022'} {rule.label}
                  </span>
                );
              })}
            </div>
          </div>
          <div><br /></div>
          <div className={`${classes.inputs} ${classes.row}`}>
            <label className={classes.formLabel}>Confirm New Password</label>
            <input
              className={classes.formControl}
              placeholder="Confirm New Password"
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
            />
            {confirmPw.length > 0 && (
              <div style={{ marginTop: '6px', textAlign: 'left' }}>
                <span style={{ fontSize: '11px', color: newPw === confirmPw ? '#27ae60' : '#C62828' }}>
                  {newPw === confirmPw ? '\u2713 Both passwords match' : '\u2022 Both passwords must match'}
                </span>
              </div>
            )}
          </div>
          <div><br /></div>
          <button
            type="submit"
            disabled={changeLoading}
            style={{
              background: changeLoading ? '#88dddd' : '#008484',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 0',
              fontSize: '16px',
              fontWeight: 600,
              cursor: changeLoading ? 'default' : 'pointer',
              width: '100%',
              textAlign: 'center',
            }}
          >
            {changeLoading ? 'Saving...' : 'Save and Continue'}
          </button>
        </form>
      ) : forgotMode ? (
        forgotSent ? (
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '20px' }}>
              If an account exists for <strong>{forgotEmail}</strong>, you will receive an email with instructions to reset your password.
            </p>
            <div className={classes.donTHaveAnAccountRegisterNow}>
              <p className={classes.labelWrapper}>
                <span
                  className={classes.label3}
                  style={{ cursor: 'pointer' }}
                  onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); setError(''); }}
                >Back to Login</span>
              </p>
            </div>
          </div>
        ) : (
          <form style={formStyle} onSubmit={handleForgotPassword}>
            {error && <div style={{color: '#ee0003', marginBottom: '12px', fontSize: '14px'}}>{error}</div>}
            <div className={`${classes.inputs} ${classes.row}`}>
              <label className={classes.formLabel}>Email Address</label>
              <input
                className={classes.formControl}
                placeholder="Enter your email address"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
              />
            </div>
            <div><br /></div>
            <button
              type="submit"
              disabled={forgotLoading}
              style={{
                background: forgotLoading ? '#88dddd' : '#008484',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 0',
                fontSize: '16px',
                fontWeight: 600,
                cursor: forgotLoading ? 'default' : 'pointer',
                width: '100%',
              }}
            >
              {forgotLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <div className={classes.donTHaveAnAccountRegisterNow} style={{ marginTop: '16px' }}>
              <p className={classes.labelWrapper}>
                <span
                  className={classes.label3}
                  style={{ cursor: 'pointer' }}
                  onClick={() => { setForgotMode(false); setError(''); }}
                >Back to Login</span>
              </p>
            </div>
          </form>
        )
      ) : (
        <>
          <form style={formStyle} onSubmit={submitForm}>
            {expiredNotice && !error && (
              <div style={{color: '#b45309', marginBottom: '12px', fontSize: '14px'}}>
                Your session expired. Please sign in again.
              </div>
            )}
            {error && <div style={{color: '#ee0003', marginBottom: '12px', fontSize: '14px'}}>{error}</div>}
            <div className={`${classes.inputs} ${classes.row}`}>
            <label className={classes.formLabel}>Email Address</label>
              <input
                  className={classes.formControl}
                  placeholder="Email Address"
                  type="text"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <br />
            </div>
            <div className={`${classes.inputs} ${classes.row}`}>
              <label className={classes.formLabel}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                    className={classes.formControl}
                    placeholder="Password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ paddingRight: '44px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    fontSize: '18px',
                    color: '#707070',
                  }}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <Button_ContentTextSizeLargeTyp
                className={classes.button2}
                text={{
                  button: <div className={classes.button}>Log in</div>,
                }}
            />
          </form>
          <div className={classes.donTHaveAnAccountRegisterNow}>
            <p className={classes.labelWrapper}>
              <span
                className={classes.label3}
                style={{ cursor: 'pointer' }}
                onClick={() => { setForgotMode(true); setError(''); }}
              >Forgot password?</span>
            </p>
          </div>
        </>
      )}
    </div>
  );
});
